// ============================================
// P0-⑤ 天气预置 7 套（2026-10-15）
// 一键写 sky（时间+太阳）+ environment（光照/雾）+ effects.weather/particles（组合式，可再微调）
// 见 P0 架构 §5.4
// ============================================
import useSceneStore from '../stores/sceneStore';
import { sunFromTime } from './sunTime';

export interface WeatherPreset {
  key: string;
  label: string;
  apply: () => void;
}

const st = () => useSceneStore.getState();

/** 写天空：启用 + manual 时间（太阳高度/方位由 sunFromTime 计算，随场景保存） */
function applySky(hours: number, extra: Record<string, unknown> = {}) {
  const sun = sunFromTime(hours);
  st().setEffects({
    sky: {
      ...st().effects.sky,
      enabled: true,
      timeMode: 'manual',
      timeHours: hours,
      sunElevation: sun.elevation,
      sunAzimuth: sun.azimuth,
      ...extra,
    },
  });
}

/** 写粒子（整对象合并，setEffects 不深合并 particles） */
function applyParticles(patch: Record<string, unknown>) {
  st().setEffects({ particles: { ...st().effects.particles, ...patch } as never });
}

export const WEATHER_PRESETS: WeatherPreset[] = [
  {
    key: 'sunny',
    label: '晴天',
    apply: () => {
      applySky(12, { lensFlareIntensity: 0.8 });
      st().setEnvironment({ ambientIntensity: 0.6, directionalIntensity: 1.4, directionalColor: '#fffbe6', fogEnabled: false });
      st().setEffects({ weather: { ...st().effects.weather, type: 'clear', intensity: 0.2 } });
      applyParticles({ enabled: false });
    },
  },
  {
    key: 'cloudy',
    label: '多云',
    apply: () => {
      applySky(11, { lensFlareIntensity: 0.3 });
      st().setEnvironment({ ambientIntensity: 0.75, directionalIntensity: 0.85, directionalColor: '#dfe8f2', fogEnabled: true, fog: { ...st().environment.fog, color: '#d7e3ee', density: 0.006 } });
      st().setEffects({ weather: { ...st().effects.weather, type: 'cloudy', intensity: 0.5 } });
      applyParticles({ enabled: false });
    },
  },
  {
    key: 'sunset',
    label: '黄昏',
    apply: () => {
      applySky(17.5, { lensFlareIntensity: 1 });
      st().setEnvironment({ ambientIntensity: 0.5, ambientColor: '#ffd9b3', directionalIntensity: 0.95, directionalColor: '#ffb066', fogEnabled: true, fog: { ...st().environment.fog, color: '#f0c9a0', density: 0.01 } });
      st().setEffects({ weather: { ...st().effects.weather, type: 'clear', intensity: 0.3 } });
      applyParticles({ enabled: false });
    },
  },
  {
    key: 'night',
    label: '夜晚',
    apply: () => {
      applySky(22, { lensFlareIntensity: 0 });
      st().setEnvironment({ ambientIntensity: 0.25, ambientColor: '#2a3a6a', directionalIntensity: 0.15, directionalColor: '#6a7bd9', fogEnabled: true, fog: { ...st().environment.fog, color: '#0a1024', density: 0.006 } });
      st().setEffects({ weather: { ...st().effects.weather, type: 'clear', intensity: 0.1 } });
      applyParticles({ enabled: false });
    },
  },
  {
    key: 'rain',
    label: '雨天',
    apply: () => {
      applySky(14, { lensFlareIntensity: 0.1 });
      st().setEnvironment({ ambientIntensity: 0.55, ambientColor: '#b9c6d4', directionalIntensity: 0.45, directionalColor: '#cfd8e3', fogEnabled: true, fog: { ...st().environment.fog, color: '#9fb0c0', density: 0.02 } });
      st().setEffects({ weather: { ...st().effects.weather, type: 'rain', intensity: 0.8, wind: [0.5, 0.2] } });
      applyParticles({ enabled: true, emitter: 'rain', count: 8000, speed: 26, size: 0.5, color: '#cfe3ff', opacity: 0.6, gravity: [0, -30, 0] });
    },
  },
  {
    key: 'fog',
    label: '雾天',
    apply: () => {
      applySky(10, { lensFlareIntensity: 0.2 });
      st().setEnvironment({ ambientIntensity: 0.7, ambientColor: '#dfe6ee', directionalIntensity: 0.55, directionalColor: '#e8eef5', fogEnabled: true, fog: { ...st().environment.fog, color: '#d5dde5', density: 0.03 } });
      st().setEffects({ weather: { ...st().effects.weather, type: 'cloudy', intensity: 0.6 } });
      applyParticles({ enabled: false });
    },
  },
  {
    key: 'snow',
    label: '雪天',
    apply: () => {
      applySky(15, { lensFlareIntensity: 0.4 });
      st().setEnvironment({ ambientIntensity: 0.65, ambientColor: '#e8f0ff', directionalIntensity: 0.8, directionalColor: '#e6efff', fogEnabled: true, fog: { ...st().environment.fog, color: '#e8eef6', density: 0.012 } });
      st().setEffects({ weather: { ...st().effects.weather, type: 'snow', intensity: 0.7, wind: [0.3, 0.1] } });
      applyParticles({ enabled: true, emitter: 'snow', count: 5000, speed: 4, size: 0.7, color: '#ffffff', opacity: 0.9, gravity: [0, -2, 0] });
    },
  },
];
